import React, { useState, useRef, useEffect } from 'react';
import { Pencil, Check, X } from 'lucide-react';

/**
 * InlineEdit — shows text with a pencil icon. On click, becomes editable textarea.
 * Props:
 *   value        - current text
 *   onSave(text) - async callback to persist
 *   className    - extra classes for the text display
 *   placeholder  - shown when empty
 *   canEdit      - boolean; renders as plain text when false (student view)
 */
export default function InlineEdit({ value = '', onSave, className = '', placeholder = 'Sin instrucciones', canEdit = true }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing) textareaRef.current?.focus(); }, [editing]);

  const handleSave = async () => {
    if (draft === value) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => { setDraft(value); setEditing(false); };

  const handleKey = (e) => {
    if (e.key === 'Escape') handleCancel();
    if (e.key === 'Enter' && e.ctrlKey) handleSave();
  };

  if (!canEdit) {
    return <p className={`text-sm text-slate-600 whitespace-pre-wrap ${className}`}>{value || placeholder}</p>;
  }

  if (editing) {
    return (
      <div className="space-y-1.5">
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={handleKey}
          rows={3}
          placeholder={placeholder}
          className="w-full text-sm border-2 border-blue-300 rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
          data-testid="inline-edit-textarea"
        />
        <div className="flex items-center gap-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50"
            data-testid="inline-edit-save-btn"
          >
            {saving ? <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" /> : <Check className="w-3 h-3" />}
            Guardar
          </button>
          <button
            onClick={handleCancel}
            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 px-2 py-1.5 rounded-lg hover:bg-slate-100 transition-colors"
            data-testid="inline-edit-cancel-btn"
          >
            <X className="w-3 h-3" /> Cancelar
          </button>
          <span className="text-[10px] text-slate-400 ml-auto">Ctrl+Enter para guardar • Esc para cancelar</span>
        </div>
      </div>
    );
  }

  return (
    <div className="group relative">
      <p className={`text-sm text-slate-600 whitespace-pre-wrap pr-6 ${className} ${!value ? 'text-slate-400 italic' : ''}`}>
        {value || placeholder}
      </p>
      <button
        onClick={() => setEditing(true)}
        className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-all"
        title="Editar instrucciones"
        data-testid="inline-edit-btn"
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
